import { uniqBy, isEmpty, isEqual } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import Tooltip from '@mui/material/Tooltip'

import lang from '@/lang'
import { RECEIPT_TYPES, payKinds, receiptStates } from '@/modules/base/util/constants'
import {
  PositionWithTaxCategory,
  PositionWithTaxCategoryList,
  PositionWithTaxCategorySum,
} from '@/modules/base/components/forms/positions-with-tax-category'
import SupplierCustomerSelect from '@/modules/base/components/generics/supplier-customer-select/supplier-customer-select'
import { TextareaInput, DateInput, TextInput, TagSelectInput, Checkbox, CurrencyInput } from '@/modules/base/components/inputs'
import { convertDateToApiDateFormat } from '@/modules/_helpers/formatDate'

import SupplierService from '@/modules/supplier/supplierService'
import checkValidReceiptNumberInput from '@/modules/receipt/_helpers/checkValidReceiptNumberInput'
import buildPositionCategory from '@/modules/base/components/forms/positions-with-tax-category/_helpers/buildPositionCategory'
import { defaultAccountTypesForExpense, defaultAccountTypesForRevenue, getChartsOfAccounts } from '@/redux/ducks/taxCategories/main'
import CustomerService from '@/modules/customer/customerService'
import { BASE_DATA_TYPES } from '@/modules/base/components/generics/constants'
import EconomicAssetFromExpense from '@/modules/economicAsset/components/_helpers/openEconomicAssetFromExpenseOrRevenue'
import ThemeInterface from '@/theme-interface'
import { addPayee } from '@/modules/receipt/_helpers/addPayee'
import { shouldShowGrossPositionsRoundingHint, shouldShowGrossTaxCalculationHint } from '@/modules/receipt/_helpers/grossTaxCalculationHint'
import { findAnalysisFactHints } from '@/modules/receipt/_helpers/analysisFactHints'
import { resolveReviewCurrency } from '@/modules/receipt/_helpers/reviewCurrency'
// FLAG:useDocuAi — finds the VAT finding that blocks the tax category (see there).
import { findUnbookableVatFact } from '@/modules/receipt/_helpers/unbookableVatFact'
// FLAG:useDocuAi — recognised benign situations from the analysis ("Notices"), rationale there.
import { findAnalysisNotices, AnalysisNotice } from '@/modules/document/utils/analysis-notices'
import { addPosition, changePosition, deletePosition, replacePositions } from '@/modules/receipt/_helpers/positions'
import ExpensePaymentConditions from '@/modules/expense/components/edit/payment-conditions/expense-payment-conditions'
import getStateAndOutstandingAmount from '@/modules/receipt/_helpers/getStateAndOutstandingAmount'
import { resetData } from '@/redux/ducks/receipt/edit'
import { PRICE_KIND } from '@/enums/transactions'
import ReceiptStateBadge from '@/modules/receipt/form/receipt-state-badge'
import { getReviewStatusBadgeLabel } from '@/modules/receipt/form/review-status-badge-label' // FLAG:useDocuAi
import ReceiptPaymentState from '@/modules/receipt/form/payment-state/receipt-payment-state'
import ReviewPaymentFields from '@/modules/document/components/review/review-payment-fields' // FLAG:useDocuAi
import { FieldMetaMap, getFieldValidation, isValueXmlLocked } from '@/modules/document/components/review/field-validation' // FLAG:useDocuAi
import ReviewFieldLabel, { resolveDateFieldCheck } from '@/modules/document/components/review/review-field-check' // FLAG:useDocuAi
import { useAppDispatch, useAppSelector } from '@/redux/store'
import GWGHelper from '@/modules/receipt/_helpers/GWGHelper'
import { openNotesAlert } from '@/modules/_helpers/openNotesAlert'
import ReviewPayeeSection from '@/modules/receipt/review/review-payee-section'
import {
  applyReviewPayeeToReceiptData,
  getReviewPayeeFromReceiptData,
  ReviewPayeeState,
} from '@/modules/receipt/review/review-payee-state'
import { hasCompleteYear } from '@/utils/date'
import { withReviewPaymentId } from '@/modules/receipt/review/review-payment-operation'
import { buildReceiptDocumentTotals, hasStatedReceiptTotals, isXmlStatedReceipt } from '@/modules/receipt/form/receipt-document-totals'
import { areReceiptPositionsXmlLocked } from '@/modules/receipt/form/xml-locked-positions'

const { OPEN: STATE_OPEN, DRAFT: STATE_DRAFT, TO_ANALYZE: STATE_TO_ANALYZE } = receiptStates
const { OPEN } = payKinds
const DEFAULT_RECEIPT_NUMBER = '-'
const deliveryInputName = 'deliveryDate'
const dateInputName = 'date'
const deliveryDateEqualsDateInputName = 'deliveryDateEqualsDate'
const priceValueNames = ['priceTotal', 'priceNet', 'priceGross']

// FLAG:useDocuAi — hover hint for an XML-locked field.
// Why not TextInput's `tooltip` prop: it renders the old CSS pseudo-tooltip ([data-tooltip] in
// styles/base/_forms.scss) — `white-space: nowrap`, left-aligned below the field, and therefore
// arbitrarily wide and over the next row. DateInput, SelectInput, and CurrencyInput show the same
// hint through MUI's standard <Tooltip>; the difference stood out when receipt invoice number and
// receipt date appeared directly below one another. Unlocked children remain untouched, so the DOM
// is then identical to before.
const XmlLockedFieldTooltip = ({ locked, className, children }: {
  locked: boolean
  className?: string
  children: React.ReactElement
}) => (locked
  ? (
    <Tooltip title={lang.documents.xmlLockedValueHint}>
      <div className={className}>{children}</div>
    </Tooltip>
  )
  : children)

function calculateIsNewReceiptWithDisabledData(state, id) {
  return id && state && ![STATE_OPEN, STATE_DRAFT, STATE_TO_ANALYZE].includes(state)
}

function isSameDay(first, second) {
  return convertDateToApiDateFormat(first) === convertDateToApiDateFormat(second)
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  disabled: {
    cursor: 'default',
  },
  card: {
    position: 'relative',
    background: theme.white,
    padding: 30,
    marginBottom: 30,
    borderRadius: 4,
    border: theme.border,
  },
  cardTitle: {
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.medium,
    marginBottom: 20,
  },
  row: {
    display: 'flex',
    margin: '20px 0px',
    '&:first-child': {
      marginTop: 0,
    },
    '&:last-of-type': {
      marginBottom: 0,
    },
    '& > *': {
      flex: '1 0 auto',
      '&:nth-child(n + 2)': {
        paddingLeft: 30,
      },
    },
  },
  stateBadge: {
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    top: -10,
    left: 0,
    width: '100%',
  },
  textInput: {
    flex: '1 0 auto',
  },
  receiptNumberInput: {
    flex: '1 0 40%',
  },
  receiptDateInput: {
    flex: '0 1 40%',
    minWidth: 150,
  },

  deliveryDateColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 60%',
    gap: 15,
  },
  mutedDateLabel: {
    '& > label': {
      color: theme.disabledColor,
    },
  },
  checkboxLabel: {
    marginLeft: 15,
    fontSize: theme.fontSizes.small,
    whiteSpace: 'nowrap',
  },
  paymentRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  highlight: {
    '& > * > input': {
      borderColor: theme.colors.yellow,
      background: theme.yellowBackground,
    },
    // date-input
    '& > * > div > input': {
      borderColor: theme.colors.yellow,
      background: theme.yellowBackground,
    },
    // text-area
    '& > textarea': {
      borderColor: theme.colors.yellow,
      background: theme.yellowBackground,
    },
  },
  // FLAG:useDocuAi — wrapper for the XML-lock tooltip: MUI attaches its hover handlers to this div.
  // It therefore inherits the flex classes of the wrapped field (which deliberately sets NO flex
  // of its own here), so the row is divided exactly as it is without the wrapper.
  xmlLockTooltipWrapper: {
    display: 'flex',
  },
  // FLAG:useDocuAi — an XML-locked field must also LOOK locked. `readOnly` alone is insufficient for
  // TextInput: its input rule fixes `color: theme.text`, while the background comes from the variant
  // — only `:disabled`, which readOnly does not set, turns it grey. DateInput and CurrencyInput map
  // their `readOnly` to the disabled attribute plus a `disabled` class and therefore are grey. This
  // is exactly how the locked receipt number stood out: it was the only field in the row that looked
  // editable (QA, 2026-08-10). Keep it local — making TextInput globally render readOnly as grey
  // would recolour every read-only field in the app.
  // The text colour uses `&&` (= .class.class input), so it also wins when the same value is currently
  // highlighted yellow as "detected" (`highlight` targets the input via `& > * > div > input`, with
  // greater specificity). The BACKGROUND deliberately remains weaker and yields to the yellow —
  // exactly like the locked receipt date next to it, whose DateInput also sets its `disabled` colour
  // via `&&`.
  xmlLockedTextInput: {
    '& input': {
      background: theme.backgroundColorDisabled,
    },
    '&& input': {
      color: theme.disabledColor,
    },
  },
  taxCalculationHint: {
    marginTop: 12,
    padding: '10px 12px',
    border: `1px solid ${theme.colors.yellow}`,
    borderRadius: theme.borderRadius,
    background: theme.yellowBackground,
    fontSize: theme.fontSizes.small,
    lineHeight: 1.4,
  },
  // FLAG:useDocuAi — the same yellow box, but as the only child of a `row`. The row is
  // `display: flex` and gives each child `flex: 1 0 auto` — `flex-shrink: 0` with an `auto` basis
  // means the box grows with the sentence and does NOT wrap, so the text ran out of the card on the
  // right. `flex: 1 1 100%` plus `minWidth: 0` gives it the row width and allows the shrinking that
  // makes wrapping possible at all (without `minWidth: 0`, a long sentence's min-content width
  // remains the minimum).
  receiptNumberNotDetectedHint: {
    flex: '1 1 100%',
    minWidth: 0,
    marginTop: 0,
  },
  // FLAG:useDocuAi — foreign currency recognised. Red (not yellow): per the review colour code a
  // detected PROBLEM is red, an identified/prefilled value is yellow — the amounts here are not
  // euros and must not be booked as such.
  foreignCurrencyHint: {
    marginTop: 12,
    padding: '10px 12px',
    border: `1px solid ${theme.colors.red}`,
    borderRadius: theme.borderRadius,
    background: theme.redBackground,
    fontSize: theme.fontSizes.small,
    lineHeight: 1.4,
  },
  // FLAG:useDocuAi — recognised benign situation ("Notice"). Deliberately NEITHER red NOR yellow:
  // under the review colour code, red is a recognised problem and yellow is a prefilled value that
  // someone should review. A notice is neither — it explains why a correct result looks different
  // than expected and therefore must not look like action is required. Blue is already the neutral
  // base colour in this UI (review-payee-section, review-payment-fields); use
  // `lighterBlueBackground` rather than `lightBlueBackground`
  // because only the former exists in BOTH
  // themes (theme.meinbuero.ts does not know the latter, leaving the notice without a background).
  analysisNoticeHint: {
    marginTop: 12,
    padding: '10px 12px',
    border: `1px solid ${theme.colors.grey}`,
    borderRadius: theme.borderRadius,
    background: theme.lighterBlueBackground,
    fontSize: theme.fontSizes.small,
    lineHeight: 1.4,
  },
}))

interface ReceiptFormInterface {
  data: any,
  isAnalysingMode: boolean
  bookings: any[]
  type: keyof typeof RECEIPT_TYPES
  readOnly: boolean
  // FLAG:useDocuAi — invoice review: locks/hides booking-only fields.
  bookingFieldsReadOnly?: boolean
  // FLAG:useDocuAi — invoice review without accounting: hides the accounting-dependent
  // sections (positions AND payment). The rule and rationale live in
  // shouldHideBookingSectionsInReview.
  hideBookingSectionsWithoutAccounting?: boolean
  openPaymentDrawer: boolean
  onChange: (data: any) => void
  onDateValidationChange?: (isValid: boolean, name: string) => void
  onRegisterDateValidation?: (name: string, validate: () => boolean) => void
  // FLAG:useDocuAi — invoice review: render the DocuAI "Zahlungsangaben"
  //  editable fields merged into the payment card.
  isDocumentInvoiceReviewMode?: boolean
  // FLAG:useDocuAi — enable ONLY the positions review tools (description column, multi-select,
  // filter, bulk tax category, reset) without entering the full invoice-review mode. Needed for
  // the expense form that has no document at all: there is no fileId, so isDocumentInvoiceReviewMode
  // must stay false — it would additionally drop the default starting position row (see positionArr
  // below) and hide the payment-conditions card, whose fields only move into the review's
  // ReviewPaymentFields when the review workspace is actually mounted.
  positionReviewTools?: boolean
  // FLAG:useDocuAi — the reviewed document's identity. Used here ONLY as the key for the position
  // list's „Zurücksetzen" snapshot: `data.id` does not exist in the review data, while all three
  // review hosts carry `documentReviewFileId`.
  documentReviewFileId?: string
  documentReviewDetails?: {
    payeeIbans?: string[] | null
    // Ingested seed for the editable debit-account field (ReviewPaymentFields falls back to
    // this until `data.debitIbans` carries the reviewer's own list).
    debitIbans?: string[] | null
    fieldMeta?: FieldMetaMap | null
    extractionConfidence?: Record<string, number> | null
    // FLAG:useDocuAi — the receipt date as stored by the ANALYSIS (null = not determined). Read only,
    // never written: `data.date` always shows a valid date because build-review-data substitutes
    // today if necessary — only comparison with this field reveals whether the value in the form
    // actually came from the receipt.
    date?: string | null
    // server-owned; authoritative whole-doc gate for the §3.5 XML lock
    reviewSource?: { source?: string | null } | null
    // FLAG:useDocuAi — drives the honest draft-badge wording (see draftBadgeLabel below).
    reviewStatus?: string | null
    // FLAG:useDocuAi — analysis findings for the reviewed receipt; read here for the tax-category
    // block (vatJurisdiction, see vatJurisdictionFact below) and the recognised benign situations
    // (notices, see analysisNotices below).
    invoiceFacts?: {
      vatJurisdiction?: {
        bookable?: boolean
        unbookableRates?: number[] | null
        isDomestic?: boolean
      } | null
      // Title/text arrive ready-made from the analysis — this UI knows no code
      // (modules/document/utils/analysis-notices.ts).
      notices?: AnalysisNotice[] | null
    } | null
  } | null
}

interface NewSupplierData {
  companyName: string,
  street?: string,
  city?: string,
  zipCode?: string
}

const ReceiptForm = (props: ReceiptFormInterface) => {
  const {
    type,
    data,
    isAnalysingMode,
    bookings,
    readOnly,
    bookingFieldsReadOnly = false,
    hideBookingSectionsWithoutAccounting = false,
    openPaymentDrawer,
    onChange,
    onDateValidationChange,
    onRegisterDateValidation,
    isDocumentInvoiceReviewMode = false,
    positionReviewTools = false,
    documentReviewFileId,
    documentReviewDetails,
  } = props
  const {
    id,
    baseDataId: payeeId,
    customerId,
    ocrDataChanged,
    payKind,
    priceKind: initialPriceKind = PRICE_KIND.GROSS,
    payee: payeeName,
    sender,
    positions = [],
    priceTotal = 0,
    state,
    supplierId,
    economicAssetId,
    date: dataDate,
    deliveryDate: dataDeliveryDate,
    description,
    receiptNumber,
    tags,
    filledData = [],
    metaData,
  } = data
  const dispatch = useAppDispatch()
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const taxCategories = useAppSelector(state => state.taxCategories.main.rawCategories)
  const temporaryReceipt = useAppSelector(state => {
    return type === RECEIPT_TYPES.EXPENSE
      ? state.expense.createTemporary.temporaryReceipt
      : state.revenue.createTemporary.temporaryReceipt
  })

  const [payee, setPayee] = useState<{ value: string, label: string, data: any, type: string }>()
  const [payeeInputValue, setPayeeInputValue] = useState(undefined) // only used after analysing receipt
  const [isNewWithDisabledData, setIsNewWithDisabledData] = useState(false)
  const [priceKind, setPriceKind] = useState(initialPriceKind)
  const [date, setDate] = useState(dataDate)
  const [deliveryDate, setDeliveryDate] = useState(dataDeliveryDate)
  const [deliveryDateEqualsDate, setDeliveryDateEqualsDate] = useState(() => !dataDeliveryDate || isSameDay(dataDeliveryDate, dataDate))
  const [identifiedValues, setIdentifiedValues] = useState((isAnalysingMode && metaData?.analysis?.indentifiedValues) || [])
  const supplierCustomerSelectRef = useRef<{
    addNew:(address: NewSupplierData) => Promise<void>,
    setValue: (option: any, type: string) => Promise<void>,
    resetModalData: () => void,
  }>()
  const isNew = !id && !temporaryReceipt
  const dateFor = deliveryDate || date
  const vatDateForRef = useRef(dateFor)
  const vatDateFor = hasCompleteYear(dateFor) ? dateFor : vatDateForRef.current
  vatDateForRef.current = vatDateFor
  const initialState = useMemo(() => state, [])
  const reset = () => {
    setPayee(null)
    setIsNewWithDisabledData(calculateIsNewReceiptWithDisabledData(state, id))
    dispatch(resetData())
  }

  useEffect(() => {
    return () => {
      dispatch(getChartsOfAccounts())
      reset()
    }
  }, [])

  useEffect(() => {
    if (!metaData?.analysis?.indentifiedValues) { return }
    if (!isEqual(metaData?.analysis?.indentifiedValues, identifiedValues)) {
      setIdentifiedValues(metaData?.analysis?.indentifiedValues)
    }
  }, [metaData?.analysis?.indentifiedValues])

  useEffect(() => {
    if (supplierId) {
      SupplierService.getById(supplierId)
        .then(supplier => {
          if (!id && supplier.notesAlert) {
            openNotesAlert(supplier.notes)
          }
          setPayee({ value: supplierId, label: payeeName || supplier.name, data: supplier, type: BASE_DATA_TYPES.SUPPLIER })
          const category = id && !isAnalysingMode ? {} : getPositionCategory(supplier.defaultBookkeepingAccountId)
          const updatedPositions = economicAssetId
            ? positions
            : positions?.map(position => ({ ...position, ...category }))
          triggerDataChange({
            payee: payeeName || supplier.name,
            baseDataId: supplierId,
            data: supplier,
            type: BASE_DATA_TYPES.SUPPLIER,
            positions: updatedPositions,
          })
        })
      return
    }

    if (customerId) {
      CustomerService.getById(customerId)
        .then(customer => {
          if (!id && customer.notesAlert) {
            openNotesAlert(customer.notes)
          }
          setPayee({ value: supplierId, label: payeeName || customer.name, data: customer, type: BASE_DATA_TYPES.CUSTOMER })
          triggerDataChange({
            payee: payeeName || customer.name,
            baseDataId: customerId,
            data: customer,
            type: BASE_DATA_TYPES.CUSTOMER,
          })
        })
    }
  }, [])

  useEffect(() => {
    if (!supplierCustomerSelectRef.current) { return }

    // if receipt was added through list upload and the supplier/customer doesn't exist, add it
    if (id && !customerId && !supplierId) {
      let supplierData: NewSupplierData = { companyName: payeeName || sender }
      if (metaData?.analysis?.data) {
        const {
          analysis: {
            data: {
              payeeStreet: street,
              payeeCity: city,
              payeeZipCode: zipCode,
            },
          },
        } = metaData

        supplierData = {
          ...supplierData,
          street,
          city,
          zipCode,
        }
      }
      supplierCustomerSelectRef.current?.addNew(supplierData)
    }

  }, [id, customerId, supplierId, supplierCustomerSelectRef])

  useEffect(() => {
    setIsNewWithDisabledData(calculateIsNewReceiptWithDisabledData(state, id))
  }, [id, state])

  useEffect(() => {
    if (isEmpty(data)) {
      reset()
    }
  }, [data])

  const selectedPayee = useMemo(() => {
    if (!payee || !payee?.data) { return null }

    const { data: { baseDataId, id, type, name: payeeName } } = payee
    return {
      value: baseDataId || id,
      label: payeeName,
      data: payee.data,
      type,
    }
  }, [payee, payeeId])

  useEffect(() => {
    if (!ocrDataChanged) { return }

    const {
      payeeStreet: street,
      payeeCity: city,
      payeeZipCode: zipCode,
      supplier,
      customer,
      ocrPayee,
      payee,
      sender,
      date: ocrDate,
      deliveryDate: ocrDeliveryDate,
    } = data
    const accountHolder = supplier?.bankAccounts?.[0]?.accountHolder
    const companyName = ocrPayee || payee || sender || accountHolder

    // update positions category
    if (supplier?.defaultBookkeepingAccountId && !economicAssetId) {
      const category = getPositionCategory(supplier.defaultBookkeepingAccountId)
      const updatedPositions = positions.map(position => ({ ...position, ...category }))
      triggerDataChange(updatedPositions)
    }

    if (date !== ocrDate) {
      setDate(ocrDate)
    }

    if (deliveryDate !== ocrDeliveryDate) {
      setDeliveryDate(ocrDeliveryDate)
    }

    setDeliveryDateEqualsDate(isSameDay(ocrDeliveryDate, ocrDate))

    if (!supplierCustomerSelectRef?.current) { return }
    if (supplier?.id || customer?.id) {
      setPayeeInputValue(undefined)
      const option = supplier ? { ...supplier, type: BASE_DATA_TYPES.SUPPLIER } : { ...customer, type: BASE_DATA_TYPES.CUSTOMER }
      supplierCustomerSelectRef.current?.setValue(option, option.type)
    } else {
      setPayeeInputValue(companyName)
      supplierCustomerSelectRef.current?.addNew({ companyName, street, city, zipCode })
    }
  }, [ocrDataChanged])

  const triggerDataChange = (newData) => {
    onChange?.(newData)
  }
  const reviewPayee = getReviewPayeeFromReceiptData(data)
  const handleReviewPayeeChange = (nextReviewPayee: ReviewPayeeState) => {
    triggerDataChange(applyReviewPayeeToReceiptData({}, nextReviewPayee))
  }

  const handleDateInputChange = (value, name) => {
    // FYI: handling the dates in state is necessary because of the current date input behaviour
    const changedDates = { [name]: value }

    if (name === dateInputName) {
      setDate(value)
      if (deliveryDateEqualsDate) {
        changedDates[deliveryInputName] = value
        setDeliveryDate(value)
      }
    } else if (name === deliveryInputName) {
      setDeliveryDate(value)
    }

    const changedNames = Object.keys(changedDates)
    setIdentifiedValues(identifiedValues.filter(field => !changedNames.includes(field)))
    triggerDataChange({
      ...changedDates,
      filledData: filledData?.filter(field => !changedNames.includes(field)),
    })
  }
  const handleDeliveryDateEqualsDateChange = (checked) => {
    setDeliveryDateEqualsDate(checked)
    if (!checked) { return }

    handleDateInputChange(date, deliveryInputName)
  }

  const handleInputChange = (value, name) => {
    const newState: { [key: string]: string | number } = { [name]: value }

    // only set the outstanding amount equal to priceTotal if the receipt is open
    if (name === 'priceTotal' && payKind === OPEN) {
      newState.outstandingAmount = value
    }

    newState.filledData = filledData.filter(field => field !== name)
    setIdentifiedValues(identifiedValues.filter(value => value !== name))
    triggerDataChange(newState)
  }
  const handleTagsChange = (tags) => {
    handleInputChange((tags || []).map(tag => tag.label), 'tags')
  }
  const handlePaymentAdd = (payment) => {
    // regenerate tempIds
    // FLAG:useDocuAi — a stable operation id makes the ONE review-save request safe to retry
    // after a lost response. Legacy receipt forms do not gain this field.
    const paymentWithRetryId = withReviewPaymentId(payment, isDocumentInvoiceReviewMode)
    const newBookings = [...bookings, { ...paymentWithRetryId, isNew: true }]
      .map((booking, index) => ({ ...booking, tempId: index }))
    const { state, outstandingAmount } = getStateAndOutstandingAmount(newBookings, priceTotal, type === RECEIPT_TYPES.EXPENSE)

    triggerDataChange({
      state,
      outstandingAmount,
      bookings: newBookings,
    })
  }
  const handlePaymentRemove = (tempId) => {
    // filter tempIds
    const newBookings = bookings.filter((booking) => booking.tempId !== tempId)
    const { state, outstandingAmount } = getStateAndOutstandingAmount(newBookings, priceTotal, type === RECEIPT_TYPES.EXPENSE)

    triggerDataChange({
      state,
      outstandingAmount,
      bookings: newBookings,
    })
  }
  const handlePaymentUnlinked = (bookingId) => {
    const newBookings = bookings.filter((booking) => booking.id !== bookingId)
    const { state, outstandingAmount } = getStateAndOutstandingAmount(newBookings, priceTotal, type === RECEIPT_TYPES.EXPENSE)

    triggerDataChange({
      state,
      outstandingAmount,
      bookings: newBookings,
    })
  }
  const handleAssignment = (assignedBooking) => {
    // regenerate tempIds and remove duplicate bookings
    const assignmentWithRetryId = withReviewPaymentId(assignedBooking, isDocumentInvoiceReviewMode)
    const newBookings = uniqBy([{
      ...assignmentWithRetryId,
      isNew: true,
    }, ...bookings], 'id')
      .map((booking, index) => ({ ...booking, tempId: index }))
    const { state, outstandingAmount } = getStateAndOutstandingAmount(newBookings, priceTotal, type === RECEIPT_TYPES.EXPENSE)

    triggerDataChange({
      state,
      outstandingAmount,
      bookings: newBookings,
    })
  }
  const onPayeeAdd = ({ type, ...addressData }) => {
    const { companyName, companyNameAffix } = addressData
    const payeeOption = { value: null, label: (`${companyName} ${companyNameAffix || ''}`).trim(), data: { addresses: [addressData] }, type }

    // remove highlighting if user made manual changes
    const newState = {
      ...addPayee(payeeOption),
      filledData: filledData.filter(field => field !== 'payee' && field !== 'supplierId'),
    }

    setIdentifiedValues(identifiedValues.filter(field => field !== 'payee' && field !== 'supplierId'))
    triggerDataChange(newState)
  }
  const onPayeeAddCancel = () => {
    triggerDataChange({
      ...addPayee({}),
      filledData: filledData.filter(field => field !== 'payee' && field !== 'supplierId'),
    })
    setIdentifiedValues(identifiedValues.filter(field => field !== 'payee' && field !== 'supplierId'))
    supplierCustomerSelectRef.current.resetModalData()
    setPayeeInputValue(undefined)
    setPayee(null)
  }
  const getPositionCategory = (defaultBookkeepingAccountId) => {
    const category = taxCategories.find(fav => fav.id === defaultBookkeepingAccountId)
    if (!category) return {}
    return buildPositionCategory(category, deliveryDate || date)
  }
  const onPayeeChanged = (payeeOption) => {
    const { data: payeeData } = payeeOption

    const updatedData = {
      ...addPayee(payeeOption),
      filledData: payeeData?.name !== payeeName ? filledData.filter(field => field !== 'payee' && field !== 'supplierId') : filledData,
    }
    if (payeeData?.name !== payeeName) {
      setIdentifiedValues(identifiedValues.filter(field => field !== 'payee' && field !== 'supplierId'))
    }

    if (!payeeOption || isEmpty(payeeOption)) {
      setPayeeInputValue(undefined)
    }

    setPayee(isEmpty(payeeOption) ? null : payeeOption)
    if (!payeeData?.defaultBookkeepingAccountId) {
      return triggerDataChange(updatedData)
    }

    const category = getPositionCategory(payeeData.defaultBookkeepingAccountId)
    const updatedPositions = economicAssetId
      ? positions
      : positions.map(position => ({ ...position, ...category }))
    triggerDataChange({ ...updatedData, positions: updatedPositions })
  }
  const onPaymentConditionsChange = ({ dueDate, payKindId }, manuallyChanged = true) => {
    const newState = {
      payKindId,
      dueDate,
    }
    if (dueDate !== data.dueDate && manuallyChanged) {
      setIdentifiedValues(identifiedValues?.filter(value => value !== 'dueDate') ?? [])
      newState.filledData = filledData?.filter(field => field !== 'dueDate') ?? []
    }

    if (payKindId !== data.payKindId && manuallyChanged) {
      setIdentifiedValues(identifiedValues?.filter(value => value !== 'payKindId') ?? [])
      newState.filledData = filledData?.filter(field => field !== 'payKindId') ?? []
    }

    triggerDataChange(newState)
  }
  const handlePriceKindChange = (value) => {
    setPriceKind(value)
    triggerDataChange({ priceKind: value })
  }
  const handleAddPosition = (position) => {
    const newData = addPosition(
      data,
      !data?.defaultBookkeepingAccountId
        ? position
        : {
          ...position,
          ...getPositionCategory(data.defaultBookkeepingAccountId),
        },
    )

    triggerDataChange(newData)
  }
  // FLAG:useDocuAi — in the review the totals belong to the DOCUMENT, not to the positions.
  // calculateReceiptPrices recomputes priceTotal/priceNet from the positions on every position
  // mutation (and the position rows fire one on mount), so the invoice's own figures were silently
  // replaced by Σ positions and then persisted by the save as documentInvoiceDetails.totalAmount —
  // a UPS invoice stating 575,71 € was stored as 4.285,00 €, the sum of its lines BEFORE the
  // rebates. The reviewer's job is to compare the two, so the stated values must survive; only an
  // explicit edit (handleChangeDocumentTotal) changes them. Legacy callers are untouched.
  // FLAG:useDocuAi — T16: the same rule for the BOOKED receipt derived from a review
  // (documentReviewDetails present): its totals are the document's, not Σ positions.
  const hasStatedTotals = hasStatedReceiptTotals({
    enabled: isDocumentInvoiceReviewMode || positionReviewTools,
    isDocumentInvoiceReviewMode,
    documentReviewDetails,
    data,
  })
  const keepDocumentTotals = (newData) => (hasStatedTotals
    ? { ...newData, priceTotal: data.priceTotal, priceNet: data.priceNet }
    : newData)
  const handleChangePosition = (position, index) => {
    const newData = changePosition(data, position, index)
    triggerDataChange(keepDocumentTotals(newData))
  }
  // FLAG:useDocuAi — apply one category to several positions in a single data
  // update; thread changePosition so totals recalc once and there's no stale state.
  const handleBulkChangePositions = (updates) => {
    const newData = updates.reduce((acc, { position, index }) => changePosition(acc, position, index), data)
    triggerDataChange(keepDocumentTotals(newData))
  }
  // FLAG:useDocuAi — restore the positions to how they were when the review opened
  // (positions only; every other field on the receipt is left as-is).
  const handleResetPositions = (originalPositions) => {
    triggerDataChange(keepDocumentTotals(replacePositions(data, originalPositions)))
  }
  // FLAG:useDocuAi — the reviewer corrected one of the document's stated totals. The field name
  // is the review's own vocabulary (priceNet / vatAmount / totalAmount); totalAmount lives on the
  // receipt as priceTotal, which is what buildDocumentInvoiceDetails reads back out on save.
  const handleChangeDocumentTotal = (field, value) => {
    const key = field === 'totalAmount' ? 'priceTotal' : field
    triggerDataChange({ ...data, [key]: value })
  }
  const handleDeletePosition = (index) => {
    const newData = deletePosition(data, index)

    if (!newData.positions.length) {
      newData.filledData = filledData.filter(field => !priceValueNames.includes(field) && field !== 'vatAmount')
      setIdentifiedValues(identifiedValues.filter(field => !priceValueNames.includes(field) && field !== 'vatAmount'))
    }

    triggerDataChange(keepDocumentTotals(newData))
  }
  const paymentDetails = useMemo(() => {
    return (
      <div className={classes.card}>
        <div className={classes.paymentRow}>
          <ReceiptPaymentState
            type={type}
            data={data}
            bookings={bookings}
            readOnly={readOnly || bookingFieldsReadOnly}
            openPaymentDrawer={openPaymentDrawer}
            onAdd={handlePaymentAdd}
            onAssign={handleAssignment}
            onRemove={handlePaymentRemove}
            onUnlink={handlePaymentUnlinked}
            onPayDateChange={handleInputChange}
            onDateValidationChange={onDateValidationChange}
            onRegisterDateValidation={onRegisterDateValidation} />
        </div>
        {/* FLAG:useDocuAi — the document's stated payment values (editable, persist on
            save) live inside the Payment card in the invoice-review flow. Never
            rendered outside review mode, so the legacy Payment card is unchanged.   */}
        {isDocumentInvoiceReviewMode && (
          <ReviewPaymentFields
            data={data}
            documentInvoiceDetails={documentReviewDetails}
            onChange={triggerDataChange}
            readOnly={readOnly}
          />
        )}
      </div>
    )
  }, [type, data, bookings, readOnly, bookingFieldsReadOnly, openPaymentDrawer, economicAssetId, isDocumentInvoiceReviewMode, documentReviewDetails])

  const showFillAnimation = (names) => {
    if (initialState !== receiptStates.DRAFT && id && !ocrDataChanged) { return false }
    return names.some(name => filledData.includes(name))
        || names.some(name => identifiedValues.includes(name))
  }

  const disabled = state && state !== STATE_OPEN
  // FLAG:useDocuAi — §3.5 XML lock: on an XML e-invoice the mapped values are
  // read-only. reviewSource is the server-owned gate (mirrors update.js's
  // enforceXmlLockedFields); metaData.invoiceAnalysisSource is the fallback for
  // rows written before reviewSource existed. The payee/SupplierCustomerSelect
  // stays editable — it carries the supplier ASSIGNMENT, not the XML name.
  const xmlLockedDocument = isDocumentInvoiceReviewMode
    && isXmlStatedReceipt(documentReviewDetails, data)
  // FLAG:useDocuAi — the line items of an XML e-invoice are locked on BOTH surfaces that show
  // them, the review and the booked receipt derived from it; the rule and its rationale live in
  // xml-locked-positions.ts, next to the totals rule it is modelled on.
  const xmlLockedPositions = areReceiptPositionsXmlLocked({
    enabled: isDocumentInvoiceReviewMode || positionReviewTools,
    documentReviewDetails,
    data,
  })
  const reviewFieldMeta = isDocumentInvoiceReviewMode ? documentReviewDetails?.fieldMeta : null
  const receiptNumberXmlLocked = isValueXmlLocked(reviewFieldMeta?.invoiceNumber, xmlLockedDocument)
  const dateXmlLocked = isValueXmlLocked(reviewFieldMeta?.date, xmlLockedDocument)
  // FLAG:useDocuAi — one lock for the document's stated totals, shared by the totals block and the
  // total field shown without accounting.
  const documentTotalsLocked = xmlLockedDocument || (hasStatedTotals && isXmlStatedReceipt(documentReviewDetails, data))
  // FLAG:useDocuAi — review markings for header fields. Until now this form read `reviewFieldMeta`
  // ONLY for the XML lock: every verdict DocuAI stamped on receipt date/invoice number/delivery date
  // travelled all the way to the client and then was not shown — while the receipt chat announced
  // those exact fields to the reviewer as requiring review (DMS issue a7285ab1 /
  // Casebook 0085). Outside the review, both sources are null ⇒ treatment null ⇒ DOM identical to before.
  const reviewFieldConfidence = isDocumentInvoiceReviewMode ? documentReviewDetails?.extractionConfidence : null
  const fieldCheck = (field: string) => getFieldValidation(reviewFieldMeta, reviewFieldConfidence, field)
  // Missing analysis dates remain empty and marked until the reviewer supplies them.
  const dateCheckOptions = {
    inReview: !!isDocumentInvoiceReviewMode,
    analysisDate: documentReviewDetails?.date,
  }
  const dateFieldCheck = resolveDateFieldCheck(fieldCheck('date'), dateCheckOptions)
  const deliveryDateFieldCheck = resolveDateFieldCheck(fieldCheck('deliveryDate'), dateCheckOptions)
  const receiptNumberFieldCheck = fieldCheck('invoiceNumber')
  // FLAG:useDocuAi — the analysis found NO invoice
  // number. As with a missing receipt date, the form does not
  // substitute anything here; the required field remains empty and marked red — which only tells
  // the reviewer „something's missing here", not „we searched and found nothing". The distinction
  // matters because some receipts carry no number at all: the reported insurance premium invoice
  // prints it only as a margin string and barcode, without a label, and leaving it empty is the
  // correct answer rather than searching two pages (issue 7bf92ff5). The hint disappears as soon as
  // someone enters a number.
  // The hint literally says "… and marked for review". It therefore may appear ONLY when the field
  // is actually marked: since DocuAI no longer treats the invoice number as required on a small-value
  // invoice (§ 33 UStDV, up to €250 gross), a till receipt or fuel receipt carries no verdict at all
  // — „no number set" is normal there, not a finding. The boundary deliberately does NOT sit here:
  // DocuAI decides whether it is required (contract_payload._is_kleinbetragsrechnung), and the client
  // follows the verdict so the threshold cannot drift between two places.
  const showReceiptNumberNotDetectedHint = !!isDocumentInvoiceReviewMode
    && !!receiptNumberFieldCheck.treatment
    && !documentReviewDetails?.invoiceNumber
    && (!receiptNumber || receiptNumber === DEFAULT_RECEIPT_NUMBER)
  // FLAG:useDocuAi — the analysis facts behind the rounding hints (taxCalculation) and the
  // assumed-line-rate hint (lineTaxAssumed, contractToAnalysis' getVatPercent fallback, see
  // assumedLineTaxHint). Until plan omxweb-draft-receipt-instead-of-review-row T20 (2026-09-18) both
  // were read from metaData.invoiceFacts in review mode ONLY — so the booked expense/revenue form,
  // which since T16 shows the very same stated-totals block, never explained a gross-derived or
  // assumed drift. Found the way the VAT-jurisdiction block is found (unbookableVatFact.ts):
  // receipt, reviewed details, attached files. `positionReviewTools` IS useDocuAi, so flag-OFF and
  // legacy forms get null by construction and render exactly as before.
  const analysisRelatedFiles = [...(data?.filesToAssign || []), ...(data?.receipts || [])]
  const { taxCalculation: taxCalculationFact, lineTaxAssumed: assumedLineTaxFact } = findAnalysisFactHints(
    isDocumentInvoiceReviewMode || positionReviewTools,
    { invoiceFacts: metaData?.invoiceFacts, reviewDetails: documentReviewDetails, relatedFiles: analysisRelatedFiles },
  )
  const showGrossTaxCalculationHint = shouldShowGrossTaxCalculationHint(taxCalculationFact)
  // FLAG:useDocuAi — foreign currency recognised: the analysis flagged this invoice as issued in a foreign
  // currency (buildForeignCurrencyInvoiceFacts → invoiceFacts.conversionRequired). Advisory hint —
  // the recognised amounts are NOT euros, so they must not be trusted as a EUR total; the real EUR
  // value only lands when the card/bank debit has been assigned.
  // This form is shared by BOTH review surfaces, so detect from either source:
  //   • review component (/documents/review/:id) — build-review-data threads metaData.invoiceFacts;
  //   • "Ausgabe prüfen" (expense-edit-page) — the expense itself carries NO facts, so read them
  //     off the attached file, exactly like ralf/dms-xmlhandling's findForeignCurrencyInvoice.
  // Not flag-gated by mode: only a useDocuAi analysis can ever produce invoiceFacts (the producer is
  // flag-gated — convertNatifExtractions' option defaults false and analyzeFileLegacy never sets it),
  // so a flag-OFF/legacy or EUR receipt carries no facts and renders exactly as before.
  // Renamed from foreignCurrencyRelatedFiles: the same file list also carries the VAT facts
  // (vatJurisdiction, see below) — the old name described only half of its purpose. Declared above,
  // beside the fact hints (T20), and shared by every fact lookup on this form.
  const foreignCurrencyFacts = [
    metaData?.invoiceFacts,
    ...analysisRelatedFiles.flatMap(file => [
      file?.documentInvoiceDetails?.invoiceFacts,
      file?.metaData?.invoiceFacts,
      file?.metaData?.ocr?.invoiceFacts,
    ]),
  ].find(facts => facts?.conversionRequired === true)
  const showForeignCurrencyHint = !!foreignCurrencyFacts
  // FLAG:useDocuAi — final invoice with a fully offset advance payment (DocuAI issue ba2af6af,
  // Motel One folio). DocuAI deterministically stamps the finding on the receipt total
  // (invoice_adjustments.classify_adjustments → validationCode 'settled_by_prepayment'), and the
  // contract carries both code AND text all the way here. Without this block, the explanation
  // appeared only in the total row's TOOLTIP — the reviewer saw €0.00, eight line items, and no
  // reason. The text explicitly says what to DO: do not book it; the expense and input tax are on
  // the advance-payment invoice. It uses the same presentation as the foreign-currency and VAT
  // hints because it is the same kind of statement: a recognised receipt state, not a prefilled
  // value.
  const settledByPrepaymentNote = reviewFieldMeta?.totalAmount?.validationCode === 'settled_by_prepayment'
    ? reviewFieldMeta?.totalAmount?.validationNote
    : null
  const foreignCurrencyAmountText = [foreignCurrencyFacts?.originalGrossAmount, foreignCurrencyFacts?.originalCurrency]
    .filter(Boolean)
    .join(' ') || lang.documents.reviewForeignCurrency.title
  // FLAG:useDocuAi — the receipt currency for EVERY amount field in the review. Without it, every
  // amount hard-coded „€" (CurrencyInput.defaultProps.symbol / config.currencyFormat), including on
  // a USD invoice. Only review mode knows a currency: legacy `data.currency` is undefined, so
  // resolveReviewCurrency falls back to euros and the expense form renders unchanged.
  // First argument = structural flag gate (like findUnbookableVatFact below): both review modes use
  // useDocuAi, and with flag-OFF resolveReviewCurrency GUARANTEES euros — even if a file written
  // while the flag was ON still carries invoiceFacts.conversionRequired.
  const reviewCurrency = resolveReviewCurrency(
    isDocumentInvoiceReviewMode || positionReviewTools,
    isDocumentInvoiceReviewMode ? data?.currency : null,
    foreignCurrencyFacts,
  )
  // Foreign currency ⇒ no tax category. The selection IS the booking trigger here (the final save
  // passes the line items to bookingApi, which supports only euros), so it is locked rather than
  // failing afterwards. The authoritative block still lives server-side in
  // registerReviewedExpenseInBooking — this is the honest UI counterpart.
  const lockCategoryForeignCurrency = isDocumentInvoiceReviewMode && reviewCurrency.isForeign
  // FLAG:useDocuAi — second booking block, for the same reason as above: bookingApi represents only
  // GERMAN VAT. The finding arrives ready-made from the analysis (invoiceFacts.vatJurisdiction,
  // built in api/services/_helpers/vatJurisdiction) — nothing is re-derived here, so analysis and
  // UI cannot drift apart. The fact exists only when a rate truly is not bookable.
  // Until 2026-08-05 the block was tied to
  // isDocumentInvoiceReviewMode, making it ineffective on the
  // very expense/revenue form that performs the booking (reported on an Austrian invoice with 20%:
  // the review blocked it, but the resulting expense in the „Offen" state offered the category
  // again). Searching every source and the flag gate now live in findUnbookableVatFact (rationale
  // there); `positionReviewTools` IS useDocuAi, both gates default to false for flag-OFF/legacy, so
  // the expression is null. The authoritative block lives server-side in
  // api/services/_helpers/validateVatJurisdictionCategory — this is the honest UI counterpart.
  const vatJurisdictionFact = findUnbookableVatFact(isDocumentInvoiceReviewMode || positionReviewTools, {
    invoiceFacts: metaData?.invoiceFacts,
    reviewDetails: documentReviewDetails,
    relatedFiles: analysisRelatedFiles,
  })
  const lockCategoryUnbookableVat = vatJurisdictionFact?.bookable === false
  // FLAG:useDocuAi — recognised benign situations
  // for this receipt. The same sources and flag gate
  // as the VAT block above ensure the same receipt says the same thing in the review component and
  // the expense/revenue form. Nothing is evaluated here — recognition belongs to the analysis
  // (invoiceFacts.notices, set in contractToAnalysis); this code only finds and labels it.
  const analysisNotices = findAnalysisNotices(isDocumentInvoiceReviewMode || positionReviewTools, {
    invoiceFacts: metaData?.invoiceFacts,
    reviewDetails: documentReviewDetails,
    relatedFiles: analysisRelatedFiles,
  })
  const unbookableVatHint = lockCategoryUnbookableVat
    ? lang.taxCategoryPositions.unbookableVatCategoryHint(
      vatJurisdictionFact?.unbookableRates,
      vatJurisdictionFact?.isDomestic,
    )
    : undefined
  // FLAG:useDocuAi — in the review an empty position list is a STATEMENT ("I deleted these"), not
  // a gap to fill. Synthesizing a default row from the document total puts a figure on screen that
  // nobody entered — and the row's own mount effect writes it straight back into data.positions, so
  // the save persists a phantom position carrying the whole invoice total. Legacy keeps its default
  // row (a new expense has to start somewhere).
  const positionArr = positions?.length
    ? positions
    : (isDocumentInvoiceReviewMode ? [] : [PositionWithTaxCategory({ totalGross: priceTotal, date })])
  const positionData = { ...data, positions: positionArr }
  // FLAG:useDocuAi — second, independent reason for the rounding hint (issue fa20e48b,
  // Telekom-Mobilfunkrechnung): the document prints GROSS line amounts, so the net/VAT
  // shown per position were computed here and rounded per line. Counted off the rows the
  // reviewer actually sees — with a single gross line no drift is possible, which keeps
  // the Strato case (issue 94222e33) silent. Declared after positionArr, which it reads.
  const grossPositionCount = positionArr.filter(position => Number.isFinite(Number(position?.totalGross))).length
  const showGrossPositionsRoundingHint = shouldShowGrossPositionsRoundingHint(taxCalculationFact, grossPositionCount)
  const receiptNumberInputError = useMemo(() => checkValidReceiptNumberInput(receiptNumber) ? null : lang.receipt.form.errors.receiptNumberInvalid, [receiptNumber])

  const showDraftBadge = initialState === receiptStates.DRAFT || (!id && filledData?.length)
  // FLAG:useDocuAi — this card is the document review surface, so its badge mirrors the persisted
  // review status used by the document thumbnail. Only flag-ON review hosts supply these details;
  // all other callers keep the legacy receipt-state label by construction.
  const draftBadgeLabel = getReviewStatusBadgeLabel(documentReviewDetails?.reviewStatus)
  return (
    <div className={clsx(disabled && classes.disabled)}>
      <div className={classes.card}>
        <div className={classes.stateBadge}>
          {showDraftBadge
            ? <ReceiptStateBadge state={receiptStates.DRAFT as keyof typeof receiptStates} labelOverride={draftBadgeLabel} />
            : null
          }
        </div>
        <div className={classes.cardTitle}>{lang.receipt.form.title.detail}</div>
        <div className={classes.row}>
          {isDocumentInvoiceReviewMode
            ? (
              <ReviewPayeeSection
                value={reviewPayee}
                onChange={handleReviewPayeeChange}
                readOnly={readOnly}
                documentDirection={data?.documentDirection}
                recognizedParties={data?.recognizedParties}
                vatIdAmbiguous={data?.vatIdAmbiguous}
              />
            )
            : (
              <div>
                <SupplierCustomerSelect
                  autoFocus
                  ref={supplierCustomerSelectRef}
                  name='payee'
                  dataQsId='receiptForm-payee'
                  disableBackdropClick
                  label={`${lang.receipt.form.payee.label}*`}
                  placeholder={lang.receipt.form.payee.placeholder}
                  loadingMessage={() => lang.receipt.form.payee.loadingMessage}
                  readOnly={isNewWithDisabledData || readOnly}
                  value={selectedPayee}
                  inputValue={payeeInputValue}
                  autoOpenMenu={false}
                  handleAdd={onPayeeAdd}
                  handleChange={onPayeeChanged}
                  handleCancel={onPayeeAddCancel}
                  showFillAnimation={showFillAnimation(['payee', 'supplierId', 'customerId'])} />
              </div>
            )}
        </div>
        <div className={classes.row}>
          <XmlLockedFieldTooltip
            locked={receiptNumberXmlLocked}
            className={clsx(classes.xmlLockTooltipWrapper, classes.textInput, classes.receiptNumberInput)}>
            <TextInput
              dataQsId='receiptForm-receiptNumber'
              name='receiptNumber'
              label={(
                <ReviewFieldLabel
                  field='invoiceNumber'
                  text={`${lang.receipt.form.receiptNumber}*`}
                  treatment={receiptNumberFieldCheck.treatment}
                  note={receiptNumberFieldCheck.note} />
              )}
              value={receiptNumber}
              readOnly={readOnly || receiptNumberXmlLocked}
              errorMessage={receiptNumberInputError}
              customClass={clsx(classes.textInput, classes.receiptNumberInput, receiptNumberXmlLocked && classes.xmlLockedTextInput, showFillAnimation(['receiptNumber']) && classes.highlight)}
              fillAnimationOptions={{ show: showFillAnimation(['receiptNumber']), color: theme.colors.yellow }}
              onChange={handleInputChange}
              selectOnFocus={receiptNumber === DEFAULT_RECEIPT_NUMBER}
              useFormik={false} />
          </XmlLockedFieldTooltip>
        </div>
        {/* A separate row, NOT another child of the row above: the header fields there use
            `flex: 1 0 30%`, and an additional child would wrap the entire row (the same reasoning
            as in review-field-check.tsx).             */}
        {showReceiptNumberNotDetectedHint && (
          <div className={classes.row}>
            <div
              className={clsx(classes.taxCalculationHint, classes.receiptNumberNotDetectedHint)}
              data-qs-id='receiptForm-receiptNumberNotDetectedHint'>
              {lang.documents.reviewFieldCheck.receiptNumberNotDetected}
            </div>
          </div>
        )}
        {/* FLAG:useDocuAi — without accounting the positions card is omitted
            (hideBookingSectionsWithoutAccounting), and with it the only place the review showed the
            document's total. A documents-only tenant sees the total in the legacy Details tab, so the
            review keeps it here. An edit goes through handleChangeDocumentTotal, like the totals block. */}
        {hideBookingSectionsWithoutAccounting && (
          <div className={classes.row}>
            <XmlLockedFieldTooltip
              locked={documentTotalsLocked}
              className={clsx(classes.xmlLockTooltipWrapper, classes.textInput)}>
              <CurrencyInput
                name='priceTotal'
                dataQsId='receiptForm-priceTotal'
                label={lang.generics.totalAmount}
                symbol={reviewCurrency.symbol}
                value={priceTotal}
                readOnly={readOnly || documentTotalsLocked}
                customClass={classes.textInput}
                useFormik={false}
                // onBlur, not onChange: CurrencyInput reports its formatted value through onChange on
                // mount, which would mark an untouched review as changed.
                onBlur={(value) => {
                  if (Number(value) !== Number(priceTotal)) {
                    handleChangeDocumentTotal('totalAmount', value)
                  }
                }} />
            </XmlLockedFieldTooltip>
          </div>
        )}
        <div className={classes.row}>
          <XmlLockedFieldTooltip
            locked={dateXmlLocked}
            className={clsx(classes.xmlLockTooltipWrapper, classes.receiptDateInput)}>
            <DateInput
              name={dateInputName}
              dataQsId={`receiptForm-${dateInputName}`}
              label={(
                <ReviewFieldLabel
                  field='date'
                  text={`${lang.receipt.form.date}*`}
                  treatment={dateFieldCheck.treatment}
                  note={dateFieldCheck.note} />
              )}
              readOnly={readOnly || dateXmlLocked}
              value={date}
              customClass={clsx(classes.receiptDateInput, showFillAnimation(['date']) && classes.highlight)}
              fillAnimationOptions={{ show: showFillAnimation(['date']), color: theme.colors.yellow }}
              disableClear={!isDocumentInvoiceReviewMode}
              required={isDocumentInvoiceReviewMode}
              useFormik={false}
              onChange={handleDateInputChange}
              onValidationChange={onDateValidationChange}
              onRegisterValidation={onRegisterDateValidation} />
          </XmlLockedFieldTooltip>
          <div className={classes.deliveryDateColumn}>
            <DateInput
              name={deliveryInputName}
              dataQsId={`receiptForm-${deliveryInputName}`}
              label={(
                <ReviewFieldLabel
                  field='deliveryDate'
                  text={`${lang.receipt.form.deliveryDate}*`}
                  treatment={deliveryDateFieldCheck.treatment}
                  note={deliveryDateFieldCheck.note} />
              )}
              readOnly={readOnly || deliveryDateEqualsDate}
              value={deliveryDateEqualsDate ? date : deliveryDate}
              customClass={clsx(deliveryDateEqualsDate && classes.mutedDateLabel, showFillAnimation(['deliveryDate']) && classes.highlight)}
              fillAnimationOptions={{ show: showFillAnimation(['deliveryDate']), color: theme.colors.yellow }}
              disableClear={!isDocumentInvoiceReviewMode}
              required={isDocumentInvoiceReviewMode}
              useFormik={false}
              onChange={handleDateInputChange}
              onValidationChange={onDateValidationChange}
              onRegisterValidation={onRegisterDateValidation} />
            <Checkbox
              name={deliveryDateEqualsDateInputName}
              dataQsId={`receiptForm-${deliveryDateEqualsDateInputName}`}
              checked={deliveryDateEqualsDate}
              disabled={readOnly}
              useFormik={false}
              onChange={handleDeliveryDateEqualsDateChange}>
              <span className={classes.checkboxLabel}>{lang.receipt.form.deliveryDateEqualsDate}</span>
            </Checkbox>
          </div>
        </div>
        <div className={classes.row}>
          <TextareaInput
            name='description'
            dataQsId='receiptForm-description'
            label={lang.receipt.form.description}
            rows={1}
            readOnly={readOnly}
            value={description || ''}
            className={clsx(showFillAnimation(['description']) && classes.highlight)}
            onChange={handleInputChange}
            fillAnimationOptions={{ show: showFillAnimation(['description']), color: theme.colors.yellow }}
            useFormik={false} />
        </div>
        {economicAssetId?.length && (
          <div className={classes.row}>
            <EconomicAssetFromExpense id={economicAssetId} />
          </div>
        )}
      </div>
      {/* FLAG:useDocuAi — positions/payment blocks are hidden in booking-fields-read-only (review) mode. */}
      {!bookingFieldsReadOnly && (
        <>
          {/* FLAG:useDocuAi — without accounting this
              section is omitted: no expense is created, so
              there is nothing to book and the line items cannot be reviewed. The line items are
              still saved; the server marks them unreviewed so they can be reviewed after the module is purchased later. The same rule removes
              the payment card below.    */}
          {!hideBookingSectionsWithoutAccounting && (
            <div className={classes.card}>
              <div className={classes.cardTitle}>{lang.receipt.form.title.positions}</div>
              <PositionWithTaxCategoryList
                type={type}
                state={initialState === receiptStates.DRAFT ? initialState : state}
                receiptId={id}
                priceTotal={priceTotal}
                positions={positionArr}
                accountType={type === RECEIPT_TYPES.EXPENSE ? defaultAccountTypesForExpense : defaultAccountTypesForRevenue}
                onChangePosition={handleChangePosition}
                onDeletePosition={handleDeletePosition}
                disableTax={isNewWithDisabledData}
                disabled={!!(readOnly || economicAssetId || bookings?.length || initialState === receiptStates.IN_PROGRESS)}
                dateFor={vatDateFor}
                isNewBookkeepingRecord={isNew}
                filledData={[...identifiedValues, ...filledData]}
                ocrDataChanged={ocrDataChanged}
                priceKind={priceKind}
                isEconomicAsset={economicAssetId}
                reviewTools={isDocumentInvoiceReviewMode || positionReviewTools}
                onBulkChangePositions={handleBulkChangePositions}
                onResetPositions={handleResetPositions}
                // FLAG:useDocuAi — the „Zurücksetzen" snapshot was keyed to `receiptId`
                // (= `data.id`). That does not exist in the review, so the snapshot was never taken
                // and the chip was missing from all three review surfaces. The document ID is the
                // identity available there; without it (expense/revenue form), the list continues
                // to fall back to `receiptId`.
                snapshotKey={documentReviewFileId}
                // FLAG:useDocuAi — XML e-invoice values are authoritative: lock VAT +
                // price, keep only the category editable. Per-line meta adds the
                // single-line lock + "why locked" tooltip (§3.5, 1c).
                lockValues={xmlLockedPositions}
                // FLAG:useDocuAi — an XML e-invoice carries exactly the lines the document
                // states. Deleting one was never persisted anyway: enforceXmlLockedFields
                // restores the stored positions wholesale on a count mismatch, so the row
                // came back on the next load. Don't offer what the save discards.
                hideDeleteButton={xmlLockedPositions}
                positionsFieldMeta={reviewFieldMeta?.positions}
                // FLAG:useDocuAi — currency code instead of „€" on every line-item amount, plus the
                // VAT amount per row (previously calculated, stored, and booked, but never shown).
                // Both are review-only; legacy keeps the defaults.
                currencySymbol={reviewCurrency.symbol}
                showVatAmount={isDocumentInvoiceReviewMode || positionReviewTools}
                lockCategoryForeignCurrency={lockCategoryForeignCurrency}
                lockCategoryUnbookableVat={lockCategoryUnbookableVat}
                unbookableVatHint={unbookableVatHint}
              />
              <GWGHelper
                positions={data.positions}
                isBanking={false}
              />
              <PositionWithTaxCategorySum
                disabled={readOnly}
                {...positionData}
                priceKind={priceKind}
                onAddPosition={handleAddPosition}
                onChangePriceKind={handlePriceKindChange}
                // FLAG:useDocuAi — same reason as hideDeleteButton: an added line changes the
                // position count, which the server-side XML lock rejects by restoring the
                // stored positions. The line set of an e-invoice is the document's, not ours.
                hideAddPosition={economicAssetId || xmlLockedPositions}
                // FLAG:useDocuAi — whole-invoice document allowance breakdown (line-item total →
                // discount → total). Only ever populated on DocuAI review/booked-expense data;
                // undefined for legacy callers, so the sum renders byte-identical to before.
                positionsTotal={data.positionsTotal}
                documentAllowance={data.documentAllowance}
                // FLAG:useDocuAi — typed BG-20/21 document allowances/charges (v1.3.0), from
                // the first-class allowanceCharges column: enrich the discount/surcharge line
                // with reason + VAT rate.
                allowanceCharges={data.allowanceCharges}
                // FLAG:useDocuAi — in the review the totals are the ones the DOCUMENT states (what
                // DocuAI read off it), editable in place; a recomputed sum would quietly cover up a
                // wrong extraction, which is exactly what the reviewer is here to catch. Only the
                // review passes these, so every other caller keeps the computed sum block.
                // T16: also for the booked receipt that carries its reviewed details — the form then
                // shows (and lets the user correct) the document's figures instead of a per-line
                // rounded sum that the document never printed.
                documentTotals={hasStatedTotals ? buildReceiptDocumentTotals(data) : undefined}
                onChangeDocumentTotal={handleChangeDocumentTotal}
                // FLAG:useDocuAi — on an XML invoice, VAT per rate is stated ON THE RECEIPT
                // (EN 16931 BG-23) and is not derived from the rows. This is exactly where the two
                // diverged: a document allowance reduces the taxable amount, while Σ rows remains
                // the full sum — net × rate then displayed excessive VAT, locked and therefore
                // impossible to correct. Gated on the same condition as the field locks
                // (xmlLockedDocument), so only XML receipts see it; every other receipt gets
                // undefined and continues summing from the line items as before.
                documentVat={xmlLockedDocument ? data.vat : undefined}
                // FLAG:useDocuAi — the receipt states multiple VAT rates but none per line item
                // (issue aaee49cf). Every line-item net amount is then based on an assumption, and
                // the line-items-versus-net hint would measure that assumption rather than the
                // receipt. Found on every path since T20, like taxCalculationFact above.
                assumedLineTax={assumedLineTaxFact}
                // FLAG:useDocuAi — on an XML e-invoice the price base is not the reviewer's
                // choice: EN 16931 states line amounts net (BT-131) and net/VAT/total on the
                // document itself. The switch changed nothing in the stated-totals block and, on
                // a click, silently persisted priceKind='gross' onto a net invoice. Same gate as
                // the locked values, so only XML-authoritative reviews lose the switch.
                hidePriceKindSwitch={xmlLockedDocument}
                // FLAG:useDocuAi — the same currency as on the line items: the totals block formats
                // through formatCurrency, whose default is also „€".
                currencySymbol={reviewCurrency.symbol}
                // FLAG:useDocuAi — the stated totals are XML-locked server-side, so show them
                // read-only (with the same "why locked" hint as receipt number/date) instead
                // of inviting an edit that the save throws away.
                lockDocumentTotals={documentTotalsLocked}
              />
              {showForeignCurrencyHint && (
                <div className={classes.foreignCurrencyHint} data-qs-id='receiptForm-foreignCurrencyHint'>
                  <strong>{lang.documents.reviewForeignCurrency.title}</strong>
                  {' '}
                  {lang.documents.reviewForeignCurrency.hint(foreignCurrencyAmountText)}
                </div>
              )}
              {!!settledByPrepaymentNote && (
                <div className={classes.foreignCurrencyHint} data-qs-id='receiptForm-settledByPrepaymentHint'>
                  <strong>{lang.documents.reviewSettledByPrepayment.title}</strong>
                  {' '}
                  {settledByPrepaymentNote}
                </div>
              )}
              {/* FLAG:useDocuAi — unbookable VAT rate. Red like the foreign currency and for the
                  same reason: this is a recognised PROBLEM, not a prefilled value. The hint must
                  appear next to the locked categories, or the reviewer sees only an inert selection
                  without an explanation.           */}
              {lockCategoryUnbookableVat && (
                <div className={classes.foreignCurrencyHint} data-qs-id='receiptForm-unbookableVatHint'>
                  {unbookableVatHint}
                </div>
              )}
              {showGrossTaxCalculationHint && (
                <div className={classes.taxCalculationHint}>
                  {lang.documents.reviewTaxCalculation.grossToNetHint}
                </div>
              )}
              {showGrossPositionsRoundingHint && (
                <div className={classes.taxCalculationHint} data-qs-id='receiptForm-grossPositionsRoundingHint'>
                  {lang.documents.reviewTaxCalculation.grossPositionsRoundingHint}
                </div>
              )}
              {/* FLAG:useDocuAi — recognised benign situations. They appear beside the LINE ITEMS
                  because that is exactly where the expectation they explain breaks ("it is
                  XML, so surely there must be line items"). A hint on another card would not have helped
                  the receipt, which was reported twice for this reason. Title and text arrive
                  unchanged from the analysis — nothing is phrased or decided per code here
                  (analysis-notices.ts).                                            */}
              {analysisNotices.map(notice => (
                <div
                  key={notice.code}
                  className={classes.analysisNoticeHint}
                  data-qs-id={`receiptForm-analysisNotice-${notice.code}`}
                >
                  {notice.title && (
                    <>
                      <strong>{notice.title}</strong>
                      {' '}
                    </>
                  )}
                  {notice.message}
                </div>
              ))}
            </div>
          )}
          {/* FLAG:useDocuAi — in the review component this whole card was a duplicate: its payment
              method says the same thing as "Zahlungsart" in payment, and its due date belongs with
              the other payment values. Both now live in the payment card (ReviewPaymentFields), so
              the card is dropped for the review only — legacy expense editing keeps it. */}
          {type === RECEIPT_TYPES.EXPENSE && !isDocumentInvoiceReviewMode && (
            <div className={classes.card}>
              <div className={classes.cardTitle}>{lang.supplier.forms.payConditionForm.title}</div>
              <ExpensePaymentConditions
                data={data}
                identifiedValues={identifiedValues}
                checkFillAnimation={showFillAnimation}
                onChange={onPaymentConditionsChange}
              />
            </div>
          )}
          {/* FLAG:useDocuAi — without accounting, the payment card is omitted completely (QA TC-0301 /
              TC-0304): it contains transfer/payment/assignment booking interfaces that a tenant
              without the module cannot use — yet they were visible, and clicking crashed the app.
              The receipt's payment details grouped into the card
              (ReviewPaymentFields) are omitted with it; they are not a separate section.     */}
          {!hideBookingSectionsWithoutAccounting && paymentDetails}
        </>
      )}
      <div className={classes.card}>
        <div className={classes.cardTitle}>{lang.receipt.form.title.tags}</div>
        <TagSelectInput
          name='tags'
          placeholder={lang.receipt.form.tags}
          value={tags?.map((item) => ({
            id: item,
            label: item,
          }))}
          category={type === RECEIPT_TYPES.EXPENSE ? 'expenses' : 'revenues'}
          readOnly={readOnly}
          includeSystemTags={true}
          onChange={handleTagsChange}
          dataQsId='receiptForm-tags'
        />
      </div>
    </div>
  )
}

export default ReceiptForm
